import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { useGetVendorsQuery } from "features/vendor/vendorSlice";
import { arrayToObject } from "utils/object-util";
import { TabsWrapper } from "components/TabsWrapper/TabsWrapper";
import PricingDetailsTab from "./PricingDetailsTab";
import OtherVendorOptionsTab from "./OtherVendorOptionsTab";
import { PricingLotDetailsDialogStyles } from "./PricingLotDetailsDialogStyles";
import NotificationProvider from "components/NotificationProvider";
import { useUpdateActivityPriceMutation, useUpdateActivityPricePerClientLotMutation, useUpdateActivityPricingManagementMutation } from "features/pricing/pricingSlice";
import { ActivityItemPriceModes } from "../../../CreateActivityAndPricing/ActivityItemPricing/PricingCalculator/PricingCalculatorConsts";

const PricingLotDetailsDialog = ({ open, onClose, pricingData, initialTab = 0 }) => {
    const classes = PricingLotDetailsDialogStyles();
    const [selectedTab, setSelectedTab] = useState(0);
    const isBundlePricing = !!pricingData?.bundleId;
    const [pricingToEdit, setPricingToEdit] = useState({});
    const [pendingPreferredVendorId, setPendingPreferredVendorId] = useState(null);
    const [updateActivityPrice, { isLoading: isSaving }] = useUpdateActivityPricePerClientLotMutation();
    const [updatePreferredVendor, { isLoading: isSavingPreferred }] = useUpdateActivityPricePerClientLotMutation();

    useEffect(() => {
        // Keep tab selection valid when dialog context changes.
        setSelectedTab(initialTab ?? 0);
    }, [open, pricingData?.id, pricingData?.bundleId, initialTab]);

    useEffect(() => {
        if (!open || !pricingData) {
            setPricingToEdit({});
            setPendingPreferredVendorId(null);
            return;
        }

        setPricingToEdit({
            id: pricingData?.activityItemPriceId ?? pricingData?.id,
            price: pricingData?.price,
            margin: pricingData?.margin,
            charge: pricingData?.charge,
            priceMode: pricingData?.priceMode,
            surchargeAmount: pricingData?.surchargeAmount,
            activityItemMaterialPriceId: pricingData?.activityItemMaterialPriceId,
            // materialPrices: pricingData?.materialPrices || [],
        });
        setPendingPreferredVendorId(pricingData?.vendorId ?? null);
    }, [open, pricingData]);

    // Pricing can only be configured against an existing vendor pricing record. State 2 "Set vendor"
    // cells have no preferred vendor (and therefore no price record) yet, so pricing cannot be saved
    // until a vendor is selected in the Other Vendor Options tab.
    const activityItemPriceId = pricingData?.activityItemPriceId ?? pricingData?.id;
    const canSavePricing = !!activityItemPriceId;

    // Pricing Details save: persists only the displayed vendor's pricing. It never changes the
    // preferred vendor — that is handled independently by the Other Vendor Options tab.
    const handleSaveChanges = async () => {
        if (!pricingData) return;

        // No price record to edit (e.g. a State 2 stub cell) — nothing to save here.
        if (!activityItemPriceId) {
            onClose();
            return;
        }

        const materialPrices = (pricingToEdit?.materialPrices || [])
            .map(mp => ({
                id: mp.activityItemMaterialPriceId,
                activityItemMaterialId: mp.activityItemMaterialId,
                cost: parseFloat(mp.cost) || 0
            }));

        const vehicleTypeIdsList = pricingData?.vehicleTypeIds ? JSON.parse(pricingData.vehicleTypeIds) : null;

        const payload = {
            activityItemPriceId,
            // The displayed vendor whose pricing we are editing. The by-activity/by-client matrix
            // may seed activityItemPriceId from a different vendor's record, so the server uses
            // this to retarget the edit to the correct vendor's price record.
            vendorId: pricingData?.vendorId,
            lotId: pricingData?.lotId,
            clientId: pricingData?.clientId,
            price: pricingToEdit?.price,
            charge: pricingToEdit?.charge,
            priceMode: pricingToEdit?.priceMode,
            surchargeAmount: pricingToEdit?.priceMode === ActivityItemPriceModes.SURCHARGE ? pricingToEdit?.surchargeAmount : null,
            vehicleTypeIdsList,
            materialPrices,
        };

        const res = await updateActivityPrice(payload);

        if (!res?.error) {
            NotificationProvider.success("Updated successfully");
            onClose();
        } else {
            NotificationProvider.error("Failed to update");
        }
    };

    // Other Vendor Options: selecting a vendor persists the preferred vendor immediately
    // (preferred-vendor-only write — does not touch any pricing row), so there is no Save
    // button on this tab. Cache invalidation refreshes the grid and the options list.
    const handleSelectPreferredVendor = async (vendorId) => {
        if (!pricingData) return;

        const vehicleTypeIdsList = pricingData?.vehicleTypeIds ? JSON.parse(pricingData.vehicleTypeIds) : null;

        const res = await updatePreferredVendor({
            activityItemPriceId: 0,
            activityItemId: pricingData?.activityItemId,
            clientId: pricingData?.clientId,
            lotId: pricingData?.lotId,
            vehicleTypeIdsList,
            preferredVendorId: vendorId,
        });

        if (!res?.error) {
            setPendingPreferredVendorId(vendorId);
            NotificationProvider.success("Preferred vendor updated");
            onClose();
        } else {
            NotificationProvider.error("Failed to update preferred vendor");
        }
    };

    let { data: lots } = useGetLotsQuery();
    let { data: vendors } = useGetVendorsQuery();
    lots = lots || [];
    vendors = vendors || [];
    const vendorLookup = arrayToObject(vendors);
    const lotLookup = arrayToObject(lots);

    const tabs = [
        {
            label: "Pricing Details",
            component: <PricingDetailsTab pricingData={pricingData} pricingToEdit={pricingToEdit} setPricingToEdit={setPricingToEdit} vendorLookup={vendorLookup} />,
        },
        ...(!isBundlePricing
            ? [{
                label: "Other Vendor Options",
                component: <OtherVendorOptionsTab pricingData={pricingData} vendorLookup={vendorLookup} preferredVendorId={pendingPreferredVendorId} onPreferredVendorChange={handleSelectPreferredVendor} isSelecting={isSavingPreferred} />,
            }]
            : []),
    ];

    return (
        <Dialog open={open} onClose={onClose} fullWidth PaperProps={{ style: { minHeight: "700px", minWidth: "800px" } }}>
            <DialogTitle>
                {pricingData?.activityName}
                <label className={classes.subTitle}>Lot: {lotLookup[pricingData?.lotId]?.name || pricingData?.lotId} </label>
                {!isBundlePricing && (
                    <label className={classes.subTitle}>Vendor: {vendorLookup[pricingData?.vendorId]?.name || pricingData?.vendorId}</label>
                )}
            </DialogTitle>
            <DialogContent >
                <TabsWrapper tabs={tabs} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" color="primary">
                    Close
                </Button>
                {selectedTab === 0 && (
                    <Tooltip
                        title={canSavePricing ? "" : "Select a preferred vendor before configuring a price."}
                        arrow
                    >
                        <span>
                            <Button onClick={handleSaveChanges} variant="contained" color="primary" disabled={isSaving || !canSavePricing}>
                                {isSaving ? "Saving..." : "Save Changes"}
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default PricingLotDetailsDialog;
